import React from 'react';
import cx from 'classnames';
import Arrow from '../SvgIcons/Arrow';
import styles from './LinkV2.module.less';

const throwMultipleChildrenError = () => {
  return new Error(
    'If "reactRouterLink" prop is true, <Link /> only accepts a single React element as "children". You are probably attempting to pass a fragment or an array.'
  );
};

type HrefConditionalProps =
  | { role?: 'link' | undefined; href: string }
  | { role?: 'button'; href?: never };

type OnclickConditionalProps =
  | { type?: 'submit'; onClick?: never }
  | { type?: 'reset'; onClick?: never }
  | { type?: 'button'; onClick?: (e: React.MouseEvent) => void };

type ArrowConditionalProps =
  | { arrow?: false | undefined; arrowPosition?: never }
  | { arrow: true; arrowPosition?: 'prefix' | 'postfix' };

export type LinkV2Props = HrefConditionalProps &
  OnclickConditionalProps &
  ArrowConditionalProps &
  React.HTMLProps<HTMLAnchorElement> &
  React.HTMLProps<HTMLButtonElement> & {
    /** default size tries to inherit from page/parent context,
     *  but you can explicitly set it yourself using these props
     */

    sizes?:
      | 'inherit'
      | 'extraSmall'
      | 'smaller'
      | 'small'
      | 'regular'
      | 'large'
      | 'larger'
      | 'semiHuge'
      | 'huge';
    active?: boolean;
    subtle?: boolean;
    inline?: boolean;
    bold?: boolean;
    color?: 'default' | 'red' | 'black';
    variant?: 'primary' | 'secondary' | 'tertiary';
    reactRouterLink?: boolean;
    innerRef?: React.RefObject<HTMLButtonElement>;
    noUnderline?: boolean;
  };

const LinkV2: React.FC<LinkV2Props> = ({
  sizes = 'inherit',
  children,
  active,
  className,
  target,
  href,
  onClick,
  rel,
  arrow,
  arrowPosition = 'postfix',
  inline,
  subtle,
  bold,
  role = 'link',
  variant = 'primary',
  color = 'default',
  disabled,
  reactRouterLink,
  innerRef,
  type = 'button',
  noUnderline = false,
  ...rest
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return undefined;
    }
    if (!onClick) {
      return undefined;
    }
    return onClick(e);
  };

  const classes = cx(className, styles.link, {
    [styles.danger]: color === 'red',
    [styles.black]: color === 'black',
    [styles[variant]]: variant && !disabled,
    [styles[sizes]]: sizes,
    [styles.inline]: inline,
    [styles.subtle]: subtle,
    [styles.active]: active,
    [styles.button]: role === 'button',
    [styles.disabled]: disabled,
    [styles.bold]: bold,
    [styles.noUnderline]: arrow || noUnderline,
  });

  const renderWithArrow = (element: React.ReactNode): React.ReactNode => {
    if (!arrow) return element;

    return (
      <>
        {arrowPosition === 'prefix' && (
          <Arrow
            className={styles.newarrow}
            direction="left"
            showTitle={false}
            width={1.5}
            height={-1} // This is invalid but the component wants a height but we don't need it to maintain proportions with em
            unit="em"
            aria-hidden="true"
          />
        )}
        {element}
        {arrowPosition === 'postfix' && (
          <Arrow
            className={styles.newarrow}
            flip="horizontal"
            showTitle={false}
            width={1.5}
            height={-1}
            unit="em"
            aria-hidden="true"
          />
        )}
      </>
    );
  };

  if (role === 'button') {
    return (
      <>
        <button ref={innerRef} type={type} {...rest} onClick={handleClick} className={classes}>
          {renderWithArrow(children)}
        </button>
      </>
    );
  }

  if (reactRouterLink) {
    if (!React.Children.only(children)) {
      throwMultipleChildrenError();
    }
    if (React.isValidElement(children)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return React.cloneElement<any>(children, { className: classes }, renderWithArrow(children));
    }
  }

  return (
    <a {...rest} href={href} onClick={handleClick} target={target} rel={rel} className={classes}>
      {renderWithArrow(children)}
    </a>
  );
};

export default LinkV2;
